import React from 'react';
import PageShell from './PageShell';
import Seo from '../components/Seo';
import { ShoppingBag, Search, Award, Tv, ShieldAlert, Cpu, Sparkles, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

export const METIN2_SYSTEMS = [
  {
    slug: 'metin2-offline-shop',
    name: 'Çevrimdışı Pazar Sistemi (Offline Shop)',
    shortDesc: 'Sunucu kapalı olsa bile oyuncuların 7/24 pazar kurmasını ve otomatik ticaret yapmasını sağlayan C++ tabanlı yüksek performanslı pazar altyapısı.',
    icon: ShoppingBag,
    features: [
      'Gelişmiş veritabanı kilit mekanizması ile sıfır item kopyalama (dupe) riski',
      'Oyuncu oyundan çıksa dahi pazarın haritada aktif kalması',
      'Vergi sistemi, banka bakiyesi ve pazar süresi uzatma desteği',
      'Arama camı (search glass) ile tam entegrasyon'
    ],
    details: 'Metin2 sunucularının olmazsa olmazı olan Çevrimdışı Pazar sistemi, oyuncuların pazar kurup bilgisayarlarını açık bırakma zorunluluğunu ortadan kaldırır. Aegis Studios imzalı C++ core altyapısıyla sıfır syserr ve maksimum thread güvenliği sağlar.'
  },
  {
    slug: 'metin2-search-glass',
    name: 'Ticaret Camı & Arama Aynası (Search Glass)',
    shortDesc: 'Tüm haritalardaki pazarları anında tarayan, fiyat sıralaması yapan ve uzaktan satın alma imkanı sunan oyuncu dostu sistem.',
    icon: Search,
    features: [
      'Tüm aktif kanallardaki ve pazarlardaki ürünleri milisaniyeler içinde indeksleme',
      'Kategori, efsun, taş ve vnum bazlı detaylı filtreleme seçenekleri',
      'Uzaktan pazar satın alma ve ışınlanma modülü',
      'Yüksek oyunculu sunucularda lag yapmayan önbellek (cache) mimarisi'
    ],
    details: 'Oyuncuların aradıkları eşyaları pazarları tek tek gezmeden bulmasını sağlayan Ticaret Camı sistemi, pazar piyasasını canlı tutar ve ticaret hacmini artırır.'
  },
  {
    slug: 'metin2-battle-pass',
    name: 'Savaş Bileti (Battle Pass) & Görev Sistemi',
    shortDesc: 'Günlük, haftalık ve sezonluk görevler sunarak oyuncu bağlılığını artıran ve ödül havuzu oluşturan sezonluk etkinlik altyapısı.',
    icon: Award,
    features: [
      'Premium ve ücretsiz bilet seviyeleri',
      'Otomatik sıfırlanan günlük/haftalık zindan, patron ve metin kesme görevleri',
      'Sezon sonu özel kostüm, binek ve unvan ödülleri',
      'Kolay yönetilebilir AdminCP paneli üzerinden görev ekleme/düzenleme'
    ],
    details: 'Sezonluk Battle Pass sistemi oyuncuların her gün sunucuya giriş yapması için motive edici görevler sunar. Oyuncu tutundurma (retention) oranını %40 oranında artırır.'
  },
  {
    slug: 'metin2-streamer-system',
    name: 'Yayıncı & İçerik Üretici (Streamer) Sistemi',
    shortDesc: 'Yayıncıların canlı yayın durumlarını oyun içinde duyuran, otomatik yayıncı logosu ve ödül dağıtımı sağlayan etkileşim modülü.',
    icon: Tv,
    features: [
      'Karakter isminin yanında canlı yayıncı ve Twitch/YouTube ikonu',
      'Yayın başladığında tüm sunucuya otomatik geçen bağıran sohbet duyurusu',
      'Yayın izlenme süresine göre otomatik kod ve hediye dağıtımı',
      'Yayıncı yönetim paneli'
    ],
    details: 'Sunucunuzun tanıtımını yapan yayıncılara özel ayrıcalıklar tanıyarak Twitch, Kick ve YouTube yayınlarından gelen oyuncu trafiğini maksimum seviyeye çıkarın.'
  }
];

export function SystemsList() {
  return (
    <PageShell
      eyebrow="PROGRAMMATİK SİSTEMLER"
      title={<><span className="text-white">Metin2 Özel </span><span className="gold-text">Oyun Sistemleri</span></>}
      subtitle="Aegis Studios tarafından geliştirilmiş C++ Core & Python UI uyumlu yüksek performanslı oyun sistemlerimizi keşfedin."
    >
      <Seo
        title="Metin2 C++ & Python Oyun Sistemleri — Aegis Studios"
        description="Metin2 Offline Shop, Ticaret Camı, Battle Pass ve Yayıncı sistemleri. Sunucunuza özel C++ geliştirmeleri ve optimize sistem paketleri."
      />

      <section className="pb-24">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 lg:px-10 space-y-12">
          
          <div className="grid md:grid-cols-2 gap-8">
            {METIN2_SYSTEMS.map((sys) => {
              const Icon = sys.icon;
              return (
                <div key={sys.slug} className="rounded-3xl border border-white/10 bg-gradient-to-b from-[#14121b] to-[#0a090c] p-6 md:p-8 space-y-6 flex flex-col justify-between hover:border-[#e4b44e]/40 transition">
                  <div className="space-y-4">
                    <div className="w-12 h-12 rounded-2xl bg-[#e4b44e]/10 border border-[#e4b44e]/20 flex items-center justify-center">
                      <Icon className="w-6 h-6 text-[#e4b44e]" />
                    </div>
                    <div>
                      <h3 className="font-display text-xl font-bold text-white mb-2">{sys.name}</h3>
                      <p className="text-xs text-white/60 leading-relaxed">{sys.shortDesc}</p>
                    </div>

                    <ul className="space-y-2 pt-2">
                      {sys.features.map((feat, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs text-white/80">
                          <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                          <span>{feat}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="pt-4 border-t border-white/8 flex items-center justify-between">
                    <Link
                      to={`/systems/${sys.slug}`}
                      className="text-xs font-bold text-[#e4b44e] hover:underline inline-flex items-center gap-1"
                    >
                      Sistem Detaylarını İncele <ArrowRight className="w-4 h-4" />
                    </Link>
                    <Link
                      to="/contact"
                      className="text-[11px] font-extrabold uppercase px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 text-white/70 transition"
                    >
                      Teklif Al
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="p-8 rounded-3xl bg-gradient-to-r from-[#e4b44e]/15 via-[#14121b] to-black border border-[#e4b44e]/30 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-display text-xl font-bold text-white mb-2">Özel Oyun İçi Sistem Geliştirme</h3>
              <p className="text-xs text-white/70 max-w-2xl leading-relaxed">
                Aklınızdaki özgün C++ / Python sistem fikrini Aegis Studios mühendisliği ile hayata geçirin. Sıfırdan kodlama, core entegrasyonu ve sıfır syserr garantisi.
              </p>
            </div>
            <Link to="/contact" className="btn-gold h-12 px-8 rounded-xl font-extrabold text-xs tracking-widest uppercase shrink-0 inline-flex items-center gap-2">
              Projenizi Anlatın <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

        </div>
      </section>
    </PageShell>
  );
}

export function SystemDetail() {
  const { slug } = useParams();
  const system = METIN2_SYSTEMS.find((s) => s.slug === slug);

  if (!system) {
    return <SystemsList />;
  }

  const Icon = system.icon;

  return (
    <PageShell
      eyebrow="METİN2 SİSTEM DETAYI"
      title={<><span className="text-white">{system.name} </span></>}
      subtitle={system.shortDesc}
    >
      <Seo
        title={`${system.name} — Metin2 C++ Sistem Geliştirme`}
        description={system.shortDesc}
      />

      <section className="pb-24">
        <div className="max-w-[900px] mx-auto px-4 md:px-6 space-y-10">
          <div className="rounded-3xl border border-white/10 bg-[#0d0c11] p-8 md:p-12 space-y-8">
            <div className="flex items-center gap-4 border-b border-white/10 pb-6">
              <div className="w-16 h-16 rounded-2xl bg-[#e4b44e]/10 border border-[#e4b44e]/30 flex items-center justify-center shrink-0">
                <Icon className="w-8 h-8 text-[#e4b44e]" />
              </div>
              <div>
                <h1 className="font-display text-2xl font-bold text-white mb-1">{system.name}</h1>
                <p className="text-xs font-mono text-emerald-400">C++ Source & Python UI Ready · Clean Code</p>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-bold text-white text-base">Sistem Hakkında</h3>
              <p className="text-sm text-white/70 leading-relaxed">{system.details}</p>
            </div>

            <div className="space-y-4">
              <h3 className="font-bold text-white text-base">Öne Çıkan Teknik Özellikler</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {system.features.map((feat, idx) => (
                  <div key={idx} className="p-4 rounded-xl bg-white/5 border border-white/8 flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#e4b44e] shrink-0 mt-0.5" />
                    <span className="text-xs text-white/80 leading-relaxed">{feat}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
              <Link to="/systems" className="text-xs font-bold text-white/60 hover:text-white transition">
                ← Tüm Sistemlere Dön
              </Link>
              <Link to="/contact" className="btn-gold h-12 px-8 rounded-xl font-extrabold text-xs tracking-widest uppercase inline-flex items-center gap-2">
                Bu Sistemi Sunucunuza Ekleyin <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
